import React, {
  useState,
  useEffect,
  useRef,
  createRef,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import isEqual from 'lodash.isequal';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import SelectAllIcon from '@mui/icons-material/SelectAll';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AddIcon from '@mui/icons-material/Add';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import FilterMenus from './FilterMenus';
import CompTable from './CompTable/CompTable';
import SummariesTable from './CompTable/SummariesTable';
import buildCompSummaries from './CompTable/buildCompSummaries';
import EquityInputs from './EquityInputs';
import AddCompModal from './AddCompModal';
import RefreshCompsModal from './RefreshCompsModal';
import {
  EMPTY_SUBJECT_COMPANY,
  ensureSubjectCompanyFirst,
  compsForApi,
  buildCompsUserData,
  isFlagOn,
  toggleFlag,
  normalizeCompFlags,
  compIdentifier,
} from './compsHelpers';

import { copy } from '../../utils';
import { getUserId } from '../../utils/auth';
import { getAuthSession } from '../../utils/authSession';
import { ErrorMessageContext } from '../../contexts';
import useFetch from '../../hooks/useFetch';

import './index.scss';

export default function Comps({ mode = 'comps', companyToViewData }) {
  const tabSelected = mode;
  const { setShowErrorMessage } = useContext(ErrorMessageContext);

  const [userData, setUserData] = useState(() => buildCompsUserData(companyToViewData));
  const [compData, setCompData] = useState([EMPTY_SUBJECT_COMPANY]);
  const [industries, setIndustries] = useState(['']);
  const [searchIndustryInput, setSearchIndustryInput] = useState(['']);
  const [currentIndustries, setCurrentIndustries] = useState(['']);
  const [showAddCompModal, setShowAddCompModal] = useState(false);
  const [showIndustriesModal, setShowIndustriesModal] = useState(false);
  const [showRefreshCompsModal, setShowRefreshCompsModal] = useState(false);
  const [industryError, setIndustryError] = useState(false);
  const [runningComps, setRunningComps] = useState(false);
  const [compRunProgress, setCompRunProgress] = useState({});
  const [loadingComps, setLoadingComps] = useState(true);
  const [showComps, setShowComps] = useState(false);
  const [noCompsToShow, setNoCompsToShow] = useState(false);
  const [identifyingOutliers, setIdentifyingOutliers] = useState(false);
  const [identifyingOutliersSuccess, setIdentifyingOutliersSuccess] = useState(false);

  const [currentSummarySelections, setCurrentSummarySelections] = useState([]);
  const [summariesOutdated, setSummariesOutdated] = useState(false);
  const [reSummarizing, setReSummarizing] = useState(false);
  const [reSummarizeComplete, setReSummarizeComplete] = useState(false);

  const [showVolatilityFootnote, setShowVolatilityFootnote] = useState(false);
  const [showBacksolveFootnote, setShowBacksolveFootnote] = useState(false);
  const [descriptionsToShow, setDescriptionsToShow] = useState(['']);

  const checkCompStatusRef = useRef(null);
  const persistChainRef = useRef(Promise.resolve());
  const compDataRef = useRef([EMPTY_SUBJECT_COMPANY]);
  const editGenRef = useRef(0);
  const [, getCalcObjectRequest] = useFetch();
  const [, getFootnotesRequest] = useFetch();

  const [, getCompsRequest] = useFetch();
  const [, compEstimateRequest] = useFetch();
  const [, runCompRequest] = useFetch();
  const [, identifyOutliersRequest] = useFetch();
  const [, removeIndustryRequest] = useFetch();
  const [, saveFootnotesRequest] = useFetch();
  const [, updateCalcObjectRequest] = useFetch();

  const growthFilterOptions = [
    'Market growth',
    'Revenue growth',
    'EBITDA growth',
    'Gross Profit growth',
    'EBITDA Margin',
    'Gross Margin',
  ];
  const multiplesFilterOptions = ['Revenue multiples', 'EBITDA multiples'];
  const yearsFilterOptions = [...Array(40)].map((val, index) =>
    (index / 4 + 0.25 <= 1 ? `${index / 4 + 0.25} year` : `${index / 4 + 0.25} years`));
  const fiscalPeriodFilterOptions = [
    'Last fiscal year -1 (LFY-1)',
    'Last fiscal year (LFY)',
    'Last 12 months (LTM)',
    'Current fiscal year (CFY)',
    'Next 12 months (NTM)',
    'Next fiscal year (NFY)',
    'Next fiscal year +1 (NFY+1)',
    'Next fiscal year +2 (NFY+2)',
  ];

  const [filters, setFilters] = useState({
    showCompanyInfo: false,
    showRevenue: false,
    showEBITDA: false,
    showGrossProfit: false,
    showMarketCap: false,
    showVolatilityValDate: false,
    showVolatilityTrDate: false,
    growthFilterValues: [],
    multiplesFilterValues: [],
    yearsFilterValues: yearsFilterOptions,
    fiscalPeriodFilterValues: [],
  });

  // Tabs passes transactionData as companyToViewData (not the full wrapper)
  const companyId = userData.metaData?.companyId
    || companyToViewData?.transactionData?.companyId
    || companyToViewData?.companyId
    || companyToViewData?.companyData?.companyId;
  const transactionId = userData.metaData?.transactionId
    || companyToViewData?.transactionData?.transactionId
    || companyToViewData?.transactionId;

  const displaySummaries = useMemo(
    () => buildCompSummaries(compData, tabSelected),
    [compData, tabSelected],
  );

  const headerScroll = useRef(null);
  const subCompScroll = useRef(null);
  const rightCompXScroll = useRef(null);
  const rightCompYScroll = useRef(null);
  const overlayScroll = useRef(null);
  const summariesScroll = useRef(null);
  const descriptionsRef = useRef({});
  const checkboxesRef = useRef({});

  function applyLoadedComps(comps, summaryTxn) {
    let normalized = ensureSubjectCompanyFirst(comps);
    // Fill sticky subject row name from account/company when CapIQ id is missing
    const subjectName = userData.metaData?.companyName
      || userData.transactionData?.companyName
      || '';
    if (normalized[0] && !normalized[0].Name && subjectName) {
      normalized = [
        { ...normalized[0], Name: subjectName, 'Subject Company': 1 },
        ...normalized.slice(1),
      ];
    }
    compDataRef.current = normalized;
    setCompData(normalized);
    setDescriptionsToShow([...Array(normalized.length)].map(() => ''));
    descriptionsRef.current = normalized.map((_, i) => descriptionsRef.current[i] ?? createRef());
    checkboxesRef.current = normalized.map((_, i) => checkboxesRef.current[i] ?? createRef());
    setCurrentSummarySelections(
      normalized
        .slice(1)
        .map((comp) => ({
          Identifier: compIdentifier(comp),
          Active: isFlagOn(comp.Active) ? 1 : 0,
          Multiples: isFlagOn(comp.Multiples) ? 1 : 0,
          Outlier: isFlagOn(comp.Outlier) ? 1 : 0,
        }))
        .sort((a, b) => String(a.Identifier).localeCompare(String(b.Identifier))),
    );
    const hasGuidelineComps = normalized.slice(1).length > 0;
    setNoCompsToShow(!hasGuidelineComps);
    setShowComps(hasGuidelineComps);
    if (summaryTxn) {
      setUserData((prev) => ({
        ...prev,
        transactionData: {
          ...prev.transactionData,
          ...summaryTxn,
          basicInfo: {
            ...prev.transactionData.basicInfo,
            ...(summaryTxn.basicInfo || {}),
          },
        },
      }));
    }
  }

  function loadCompsFromApi({ forPoll = false } = {}) {
    if (!companyId || !transactionId) {
      setLoadingComps(false);
      setShowComps(false);
      setNoCompsToShow(true);
      return;
    }
    if (!forPoll) {
      setLoadingComps(true);
      setShowComps(false);
    }
    getCompsRequest({
      url: '/comps/get-comps',
      urlIds: [companyId, transactionId],
      onSuccess: (responseData) => {
        const txnMeta = responseData?.transaction || {};
        const status = txnMeta.compStatus;
        if (status === 'running') {
          setRunningComps(true);
          setLoadingComps(true);
          setShowComps(false);
          if (!checkCompStatusRef.current) {
            checkCompStatusRef.current = setInterval(() => {
              loadCompsFromApi({ forPoll: true });
            }, 5000);
          }
          return;
        }
        if (checkCompStatusRef.current) {
          clearInterval(checkCompStatusRef.current);
          checkCompStatusRef.current = null;
        }
        if (status === 'error') {
          setRunningComps(false);
          setLoadingComps(false);
          setCompRunProgress({});
          if (txnMeta.compError) setShowErrorMessage(String(txnMeta.compError));
          // Still try to show any saved comps
          if (responseData?.comps) applyLoadedComps(responseData.comps, txnMeta);
          else {
            setShowComps(false);
            setNoCompsToShow(true);
          }
          return;
        }
        if (responseData?.comps) {
          applyLoadedComps(responseData.comps, txnMeta);
          setRunningComps(false);
          setCompRunProgress({});
          setShowAddCompModal(false);
          setShowIndustriesModal(false);
        } else {
          setCompData([EMPTY_SUBJECT_COMPANY]);
          setNoCompsToShow(true);
          setShowComps(false);
          setRunningComps(false);
        }
      },
      onError: () => {
        if (!forPoll) {
          setCompData([EMPTY_SUBJECT_COMPANY]);
          setNoCompsToShow(true);
          setShowComps(false);
        }
      },
      onFinally: () => {
        // Clear page loader once we know status (modal still covers CapIQ run UI)
        setLoadingComps(false);
      },
    });
  }

  useEffect(() => {
    const next = buildCompsUserData(companyToViewData);
    setUserData(next);
    const industryList = next.transactionData?.basicInfo?.companyIndustry?.length
      ? next.transactionData.basicInfo.companyIndustry
      : [''];
    setIndustries(industryList);
    setSearchIndustryInput(industryList);
    setCurrentIndustries(industryList.filter(Boolean).length ? industryList : ['']);
  }, [
    companyToViewData?.companyData?.companyId,
    companyToViewData?.companyId,
    companyToViewData?.transactionData?.transactionId,
    companyToViewData?.transactionId,
  ]);

  useEffect(() => {
    loadCompsFromApi();
    return () => {
      if (checkCompStatusRef.current) {
        clearInterval(checkCompStatusRef.current);
        checkCompStatusRef.current = null;
      }
    };
  }, [companyId, transactionId]);

  // Equity scenarios and footnotes live on calc_comps_* / footnotes_*, not the 409A txn.
  // Load them once per transaction so Calculate equity matches ASC 820 after refresh.
  useEffect(() => {
    if (!companyId || !transactionId) return undefined;
    let cancelled = false;
    getCalcObjectRequest({
      url: `/calc-engine/get-calc-engine-object/${companyId}&${transactionId}`,
      onSuccess: (calcObject) => {
        if (cancelled || !calcObject) return;
        setUserData((prev) => ({
          ...prev,
          calcData: {
            ...prev.calcData,
            ...(calcObject.limits ? { limits: calcObject.limits } : {}),
            ...(calcObject.methods ? { methods: calcObject.methods } : {}),
          },
        }));
      },
    });
    getFootnotesRequest({
      url: `/footnotes/get-transaction-footnote/${companyId}&${transactionId}`,
      onSuccess: (notes) => {
        if (cancelled || !notes) return;
        const methodsFootnotes = notes.methodsFootnotes
          || notes.payload?.methodsFootnotes
          || [];
        setUserData((prev) => ({
          ...prev,
          footnoteData: {
            ...prev.footnoteData,
            volPercentFn: notes.volPercentFn ?? prev.footnoteData?.volPercentFn ?? '',
            tdVolatilityBacksolve: notes.tdVolatilityBacksolve
              ?? prev.footnoteData?.tdVolatilityBacksolve
              ?? '',
            methodsFootnotes,
          },
        }));
      },
    });
    return () => {
      cancelled = true;
    };
  }, [companyId, transactionId]);

  useEffect(() => {
    if (
      currentSummarySelections.length > 0 &&
      !isEqual(
        currentSummarySelections,
        compData
          .slice(1)
          .map((comp) => ({
            Identifier: compIdentifier(comp),
            Active: isFlagOn(comp.Active) ? 1 : 0,
            Multiples: isFlagOn(comp.Multiples) ? 1 : 0,
            Outlier: isFlagOn(comp.Outlier) ? 1 : 0,
          }))
          .sort((a, b) => String(a.Identifier).localeCompare(String(b.Identifier))),
      )
    ) {
      setSummariesOutdated(true);
    } else {
      setSummariesOutdated(false);
    }
  }, [compData, currentSummarySelections]);

  function handleRightCompXScroll() {
    if (!rightCompXScroll.current) return;
    if (headerScroll.current) headerScroll.current.scrollLeft = rightCompXScroll.current.scrollLeft;
    if (subCompScroll.current) subCompScroll.current.scrollLeft = rightCompXScroll.current.scrollLeft;
    if (overlayScroll.current) overlayScroll.current.scrollLeft = rightCompXScroll.current.scrollLeft;
    if (summariesScroll.current) {
      summariesScroll.current.scrollLeft = rightCompXScroll.current.scrollLeft;
    }
  }

  function handleOverlayScroll() {
    if (!overlayScroll.current) return;
    if (headerScroll.current) headerScroll.current.scrollLeft = overlayScroll.current.scrollLeft;
    if (subCompScroll.current) subCompScroll.current.scrollLeft = overlayScroll.current.scrollLeft;
    if (rightCompXScroll.current) {
      rightCompXScroll.current.scrollLeft = overlayScroll.current.scrollLeft;
    }
    if (summariesScroll.current) {
      summariesScroll.current.scrollLeft = overlayScroll.current.scrollLeft;
    }
  }

  function handleSummariesScroll() {
    if (!summariesScroll.current) return;
    if (headerScroll.current) headerScroll.current.scrollLeft = summariesScroll.current.scrollLeft;
    if (subCompScroll.current) subCompScroll.current.scrollLeft = summariesScroll.current.scrollLeft;
    if (rightCompXScroll.current) {
      rightCompXScroll.current.scrollLeft = summariesScroll.current.scrollLeft;
    }
    if (overlayScroll.current) overlayScroll.current.scrollLeft = summariesScroll.current.scrollLeft;
  }

  function handleRightCompYScroll() {
    if (!rightCompYScroll.current) return;
    if (
      Math.abs(
        rightCompYScroll.current.scrollHeight -
          rightCompYScroll.current.scrollTop -
          rightCompYScroll.current.clientHeight,
      ) < 16 &&
      !navigator.userAgent.includes('Firefox')
    ) {
      rightCompYScroll.current.scrollTop =
        rightCompYScroll.current.scrollHeight - rightCompYScroll.current.clientHeight - 16;
    }
  }

  const rightCompXScrollRef = useCallback((node) => {
    if (rightCompXScroll?.current) {
      rightCompXScroll.current.removeEventListener('scroll', handleRightCompXScroll);
    }
    if (node) node.addEventListener('scroll', handleRightCompXScroll, { passive: true });
    rightCompXScroll.current = node;
  }, []);

  const rightCompYScrollRef = useCallback((node) => {
    if (rightCompYScroll?.current) {
      rightCompYScroll.current.removeEventListener('scroll', handleRightCompYScroll);
    }
    if (node) node.addEventListener('scroll', handleRightCompYScroll, { passive: true });
    rightCompYScroll.current = node;
  }, []);

  const overlayScrollbarRef = useCallback((node) => {
    if (overlayScroll?.current) {
      overlayScroll.current.removeEventListener('scroll', handleOverlayScroll);
    }
    if (node) node.addEventListener('scroll', handleOverlayScroll, { passive: true });
    overlayScroll.current = node;
  }, []);

  const summariesScrollRef = useCallback((node) => {
    if (summariesScroll?.current) {
      summariesScroll.current.removeEventListener('scroll', handleSummariesScroll);
    }
    if (node) node.addEventListener('scroll', handleSummariesScroll, { passive: true });
    summariesScroll.current = node;
  }, []);

  function sortCompsByCheckedStatus(comps, currentTab) {
    if (!comps?.length) return comps;
    const headerRow = comps[0];
    const rows = comps.slice(1);
    const isChecked = (comp) => (
      currentTab === 'comps'
        ? isFlagOn(comp.Active) && isFlagOn(comp.Multiples)
        : isFlagOn(comp.Active)
    );
    const getSortName = (comp) =>
      String(comp.Name || compIdentifier(comp) || '').replace(/ *\([^)]*\) */g, '').trim();
    rows.sort((a, b) => {
      const aChecked = isChecked(a);
      const bChecked = isChecked(b);
      if (aChecked !== bChecked) return Number(bChecked) - Number(aChecked);
      return getSortName(a).localeCompare(getSortName(b), undefined, { sensitivity: 'base' });
    });
    return [headerRow, ...rows];
  }

  function selectionSnapshot(rows) {
    return compsForApi(rows)
      .map((comp) => ({
        Identifier: compIdentifier(comp),
        Active: isFlagOn(comp.Active) ? 1 : 0,
        Multiples: isFlagOn(comp.Multiples) ? 1 : 0,
        Outlier: isFlagOn(comp.Outlier) ? 1 : 0,
      }))
      .sort((a, b) => String(a.Identifier).localeCompare(String(b.Identifier)));
  }

  /**
   * Same contract as ASC 820 Re-run Summary (summarize-and-save-comps): write
   * Active / Multiples / Outlier and the remaining company list to S3.
   * Calls are chained so a slower earlier save cannot overwrite a later one.
   * Local rows stay in place unless the saved payload matches, so a bad
   * response cannot re-check or resurrect a company the user just changed.
   */
  function persistCompsToS3(nextCompData, { syncFromServer = false } = {}) {
    if (!companyId || !transactionId || !nextCompData?.length) return Promise.resolve(null);
    const rows = compsForApi(nextCompData).map((comp) => ({
      ...comp,
      Active: isFlagOn(comp.Active) ? 1 : 0,
      Multiples: isFlagOn(comp.Multiples) ? 1 : 0,
      Outlier: isFlagOn(comp.Outlier) ? 1 : 0,
    }));
    const sentSnapshot = selectionSnapshot(nextCompData);
    const gen = editGenRef.current;

    const run = async () => {
      try {
        const session = await getAuthSession();
        const JWT = session.tokens.idToken.toString();
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/comps/summarize-and-save-comps`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${JWT}`,
            },
            body: JSON.stringify({
              enterpriseCompanyId: companyId,
              portfolioCompanyId: companyId,
              transactionId,
              requestUserId: getUserId(),
              compData: rows,
            }),
          },
        );
        if (!res.ok) {
          setShowErrorMessage(`Unable to save comps (${res.status}). Try Re-run Summary.`);
          return null;
        }
        const responseData = await res.json();
        const serverComps = responseData?.comps
          ? ensureSubjectCompanyFirst(responseData.comps)
          : null;
        const serverMatches = !!serverComps
          && isEqual(sentSnapshot, selectionSnapshot(serverComps));
        // Ignore a stale response if the user already changed the list again.
        const stillCurrent = gen === editGenRef.current
          && isEqual(sentSnapshot, selectionSnapshot(compDataRef.current));
        if (stillCurrent && serverMatches) {
          setCurrentSummarySelections(sentSnapshot);
          setSummariesOutdated(false);
          if (syncFromServer) {
            applyLoadedComps(
              sortCompsByCheckedStatus(serverComps, tabSelected),
              responseData.transaction,
            );
          }
        } else if (stillCurrent && !serverMatches) {
          setShowErrorMessage('Saved comps did not match the company list. Try Re-run Summary.');
        }
        return responseData;
      } catch (e) {
        setShowErrorMessage(`Unable to save comps: ${e?.message || e}`);
        return null;
      }
    };

    const task = persistChainRef.current.then(run, run);
    persistChainRef.current = task.then(() => undefined, () => undefined);
    return task;
  }

  function applyCompDataUpdate(updater, { persist = true, sort = true } = {}) {
    const prev = compDataRef.current?.length ? compDataRef.current : [EMPTY_SUBJECT_COMPANY];
    const draft = copy(prev).map((row, i) => (i === 0 ? row : normalizeCompFlags(row)));
    const nextRaw = typeof updater === 'function' ? updater(draft) : updater;
    const next = sort ? sortCompsByCheckedStatus(nextRaw, tabSelected) : nextRaw;
    editGenRef.current += 1;
    compDataRef.current = next;
    setCompData(next);
    // Save immediately. A delayed debounce was lost when the page refreshed
    // before the timer fired, so uncheck / flag / delete did not stick.
    if (persist) persistCompsToS3(next, { syncFromServer: false });
    return next;
  }

  function setCompDataWithSort(dataOrUpdater) {
    applyCompDataUpdate(
      (draft) => (typeof dataOrUpdater === 'function' ? dataOrUpdater(draft) : dataOrUpdater),
      { persist: true, sort: true },
    );
  }

  function deleteCompById(companyIdentifier) {
    const targetId = compIdentifier(companyIdentifier);
    if (!targetId) return;
    applyCompDataUpdate(
      (draft) => draft.filter((company, index) => (
        index === 0 || compIdentifier(company) !== targetId
      )),
      { persist: true, sort: true },
    );
  }

  function reSummarize(indexChanged, typeChanged) {
    applyCompDataUpdate((draft) => {
      let newCompData = draft;
      if (indexChanged === 'all') {
        if (typeChanged === 'active') {
          const anyOn = newCompData.some((comp, i) => i > 0 && isFlagOn(comp.Active));
          newCompData = newCompData.map((comp, i) =>
            (i === 0 ? comp : { ...comp, Active: anyOn ? 0 : 1 }));
        } else {
          const anyOn = newCompData.some((comp, i) => i > 0 && isFlagOn(comp.Multiples));
          newCompData = newCompData.map((comp, i) =>
            (i === 0 ? comp : { ...comp, Multiples: anyOn ? 0 : 1 }));
        }
      } else if (typeChanged === 'add-both') {
        newCompData = newCompData.map((comp, i) =>
          (i === indexChanged ? { ...comp, Active: 1, Multiples: 1 } : comp));
      } else if (typeChanged === 'active') {
        newCompData = newCompData.map((comp, i) =>
          (i === indexChanged ? { ...comp, Active: toggleFlag(comp.Active) } : comp));
      } else if (typeChanged === 'multiple') {
        // Comps-tab checkbox: selected = Active + Multiples
        newCompData = newCompData.map((comp, i) => {
          if (i !== indexChanged) return comp;
          const currentlySelected = isFlagOn(comp.Active) && isFlagOn(comp.Multiples);
          if (currentlySelected) {
            return { ...comp, Multiples: 0 };
          }
          return { ...comp, Active: 1, Multiples: 1 };
        });
      } else {
        newCompData = newCompData.map((comp, i) =>
          (i === indexChanged ? { ...comp, Outlier: toggleFlag(comp.Outlier) } : comp));
      }
      return newCompData;
    }, { persist: true, sort: typeChanged !== 'outlier' });
  }

  function persistSummarize(nextCompData, { onDone } = {}) {
    persistCompsToS3(nextCompData || compData, { syncFromServer: true })
      .then((responseData) => { if (onDone) onDone(responseData); })
      .catch(() => { if (onDone) onDone(); });
  }

  function reSummarizeRequest() {
    setReSummarizing(true);
    const rows = compDataRef.current?.length ? compDataRef.current : compData;
    persistCompsToS3(rows, { syncFromServer: true }).finally(() => {
      setReSummarizeComplete(true);
      setReSummarizing(false);
      setTimeout(() => {
        setReSummarizeComplete(false);
        setSummariesOutdated(false);
      }, 2000);
    });
  }

  function identifyOutliers() {
    if (!companyId || !transactionId) return;
    setIdentifyingOutliers(true);
    const genAtStart = editGenRef.current;
    // Wait for an in-flight checkbox save so IQR runs on the list the user sees.
    persistChainRef.current.then(() => {
      const rows = compsForApi(compDataRef.current).map((comp) => ({ ...comp, Outlier: 0 }));
      identifyOutliersRequest({
        url: '/comps/identify-outliers',
        method: 'post',
        body: {
          companyId,
          transactionId,
          requestUserId: getUserId(),
          compData: rows,
        },
        onSuccess: (responseData) => {
          if (genAtStart !== editGenRef.current) return;
          if (responseData?.comps) {
            applyLoadedComps(
              sortCompsByCheckedStatus(ensureSubjectCompanyFirst(responseData.comps), tabSelected),
              responseData.transaction,
            );
          }
          setIdentifyingOutliersSuccess(true);
          setTimeout(() => setIdentifyingOutliersSuccess(false), 3000);
        },
        onFinally: () => setIdentifyingOutliers(false),
      });
    });
  }

  function runComps(compsToAddArg, options = {}) {
    const refresh = options.refresh === true
      || (compsToAddArg && compsToAddArg.refresh === true && !Array.isArray(compsToAddArg));
    const compsList = refresh
      ? null
      : (Array.isArray(compsToAddArg) ? compsToAddArg : []);

    if (!companyId || !transactionId) {
      setShowErrorMessage('Missing company or transaction for comps run.');
      return;
    }

    const basic = userData.transactionData?.basicInfo || {};
    const dataRows = compsForApi(compData);
    const companies = (compsList || [])
      .filter((comp) => comp.compInput)
      .map((comp) => String(comp.compInput).trim())
      .filter(Boolean);
    const verticals = (industries || []).filter(Boolean);

    let runType = 'new';
    if (refresh) runType = 'refresh';
    else if (dataRows.length > 0) runType = 'add';

    if (!refresh && !companies.length && !verticals.length) {
      setIndustryError(true);
      setShowErrorMessage('Add at least one company ID/ticker or industry.');
      return;
    }

    setRunningComps(true);
    // Keep table hidden while CapIQ run is in progress (820-style loading path)
    setShowComps(false);
    setNoCompsToShow(false);
    setLoadingComps(true);
    const requestBody = {
      companyId,
      transactionId,
      requestUserId: getUserId(),
      transactionDate: basic.transactionDate || null,
      valuationDate: basic.valuationDate || basic.transactionDate || null,
      financialStatementDate: basic.financialStatementDate || null,
      companies: companies.length ? companies : null,
      verticals: verticals.length ? verticals : null,
      runType,
      compData: dataRows.length ? dataRows : null,
    };

    compEstimateRequest({
      url: '/comps/get-comps-estimate',
      method: 'post',
      body: requestBody,
      onSuccess: (res) => {
        setCompRunProgress(res || {});
        setUserData((prev) => ({
          ...prev,
          transactionData: { ...prev.transactionData, compStatus: 'running' },
        }));
        if (!checkCompStatusRef.current) {
          checkCompStatusRef.current = setInterval(() => {
            loadCompsFromApi({ forPoll: true });
          }, 5000);
        }
        runCompRequest({
          url: refresh ? '/comps/refresh-comps' : '/comps/run-comps',
          method: 'post',
          body: requestBody,
          onSuccess: () => {
            loadCompsFromApi();
          },
          onError: () => {
            setRunningComps(false);
            setLoadingComps(false);
            setCompRunProgress({});
            // Fall back: reload whatever is already saved
            loadCompsFromApi();
            setUserData((prev) => ({
              ...prev,
              transactionData: { ...prev.transactionData, compStatus: 'error' },
            }));
            if (checkCompStatusRef.current) {
              clearInterval(checkCompStatusRef.current);
              checkCompStatusRef.current = null;
            }
          },
        });
      },
      onError: () => {
        setRunningComps(false);
        setLoadingComps(false);
        setCompRunProgress({});
        loadCompsFromApi();
      },
    });
  }

  function removeIndustryFromComp(industryToRemove) {
    const nextIndustries = industries.filter((el) => el !== industryToRemove);
    setIndustries(nextIndustries.length ? nextIndustries : ['']);
    setSearchIndustryInput(nextIndustries.length ? nextIndustries : ['']);
    setCurrentIndustries(nextIndustries.filter(Boolean));

    if (!companyId || !transactionId || !industryToRemove) return;
    const rows = compsForApi(compData);
    if (!rows.length) return;

    removeIndustryRequest({
      url: '/comps/remove-industry',
      method: 'post',
      body: {
        portfolioCompanyId: companyId,
        transactionId,
        requestUserId: getUserId(),
        industry: industryToRemove,
        compData: rows,
      },
      onSuccess: (responseData) => {
        if (responseData?.comps) {
          applyLoadedComps(
            sortCompsByCheckedStatus(ensureSubjectCompanyFirst(responseData.comps), tabSelected),
            responseData.transaction,
          );
        }
      },
    });
  }

  function saveVolatilityFootnote(field, value) {
    if (!companyId || !transactionId) return;
    saveFootnotesRequest({
      url: '/footnotes/save-footnotes',
      method: 'post',
      body: {
        companyId,
        portfolioCompanyId: companyId,
        enterpriseCompanyId: companyId,
        transactionId,
        requestUserId: getUserId(),
        dataName: field,
        payload: { [field]: value },
      },
    });
  }

  function persistCalcLimits(nextUserData) {
    if (!companyId || !transactionId) return;
    updateCalcObjectRequest({
      url: '/calc-engine/update-calc-engine-object',
      method: 'post',
      body: {
        companyId,
        transactionId,
        requestUserId: getUserId(),
        limits: nextUserData.calcData?.limits,
        methods: nextUserData.calcData?.methods,
      },
    });
  }

  const valuationDate = userData.transactionData?.basicInfo?.valuationDate || '';

  return (
    <div className="Comps">
      {tabSelected === 'comps' && (
        <div className="header-info-block">
          <h4>
            <SelectAllIcon />
            Select industries, comps &amp; equity
          </h4>
          <p>
            Add companies, select comparable companies, and weight scenarios at the bottom of
            this page before you head over
            <br />
            to the Calculations tab to calculate Backsolve, PubCo and M&amp;A methods.
            <span> Need to update the data in the company list?</span>
            <Button onClick={() => setShowRefreshCompsModal(true)}>Click here</Button>
            .
          </p>
        </div>
      )}

      {tabSelected === 'industry' && (
        <>
          <div className="header-info-block">
            <h4>
              <QueryStatsIcon />
              Choose volatility
            </h4>
          </div>
          <div className="volatility-inputs-wrapper">
            <div className="volatility-inputs-header">
              <span>Choose a volatility percentile</span>
              <p>
                The volatility percentile can be selected and edited from this tab and from the
                Calculations tab. Our system updates both tabs simultaneously. Don&apos;t worry
                about being precise, you&apos;ll be able to run a range of volatilities and make a
                final selection from the Calculations tab, plus you can edit this and rerun models
                on the Calculations tab at any time.
              </p>
            </div>
            <div className="volatility-inputs-content">
              <div className={`volatility-input-wrapper${showVolatilityFootnote ? ' show-radius' : ''}`}>
                <div className="volatility-input-top">
                  <TextField
                    label="Volatility percentile"
                    value={userData.calcData?.limits?.volatility?.quantile || ''}
                    onChange={(e) => {
                      const newData = copy(userData);
                      newData.calcData.limits.volatility.quantile = e.target.value;
                      setUserData(newData);
                      persistCalcLimits(newData);
                    }}
                    select
                    SelectProps={{
                      MenuProps: {
                        disableScrollLock: true,
                        classes: { paper: 'select-dropdown' },
                      },
                    }}
                  >
                    {[
                      { value: 'manual', label: 'Manual (enter note)' },
                      { value: 'min', label: 'Minimum' },
                      { value: '5%', label: '5th percentile' },
                      { value: '10%', label: '10th percentile' },
                      { value: '25%', label: '25th percentile' },
                      { value: '50%', label: 'Median' },
                      { value: '75%', label: '75th percentile' },
                      { value: '90%', label: '90th percentile' },
                      { value: '95%', label: '95th percentile' },
                      { value: 'max', label: 'Maximum' },
                      { value: 'mean', label: 'Average' },
                    ].map((menuItem) => (
                      <MenuItem key={menuItem.label} value={menuItem.value}>
                        {menuItem.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button onClick={() => setShowVolatilityFootnote(!showVolatilityFootnote)}>
                    {`${showVolatilityFootnote ? 'Hide' : 'Show'} footnote`}
                  </Button>
                </div>
                <AnimateHeight duration={300} height={showVolatilityFootnote ? 'auto' : 0}>
                  <div className="volatility-input-footnote">
                    <span>Volatility footnote&nbsp;</span>
                    <span>will appear in client&apos;s report. </span>
                    <TextField
                      value={userData?.footnoteData?.volPercentFn || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUserData((prev) => ({
                          ...prev,
                          footnoteData: { ...prev.footnoteData, volPercentFn: value },
                        }));
                      }}
                      onBlur={(e) => saveVolatilityFootnote('volPercentFn', e.target.value)}
                      multiline
                      maxRows={5}
                      inputProps={{ maxLength: 1000 }}
                    />
                  </div>
                </AnimateHeight>
              </div>
              <div className={`volatility-input-wrapper${showBacksolveFootnote ? ' show-radius' : ''}`}>
                <div className="volatility-input-top">
                  <TextField
                    label="Volatility for Backsolve (optional)"
                    placeholder="e.g. 25%"
                    value={
                      userData.calcData?.methods?.backsolve?.manualTransactionDateVolatility || ''
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^[0-9]*\.?[0-9]*%?$/.test(inputValue)) {
                        const newData = copy(userData);
                        newData.calcData.methods.backsolve.manualTransactionDateVolatility =
                          inputValue;
                        setUserData(newData);
                        persistCalcLimits(newData);
                      }
                    }}
                  />
                  <TextField
                    label="Term for Backsolve (optional)"
                    placeholder="e.g. 5 years"
                    value={userData.calcData?.methods?.backsolve?.manualTransactionDateTerm || ''}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^[0-9]*\.?[0-9]*%?$/.test(inputValue) || inputValue === '') {
                        const newData = copy(userData);
                        newData.calcData.methods.backsolve.manualTransactionDateTerm = inputValue;
                        setUserData(newData);
                        persistCalcLimits(newData);
                      }
                    }}
                  />
                  <Button onClick={() => setShowBacksolveFootnote(!showBacksolveFootnote)}>
                    {`${showBacksolveFootnote ? 'Hide' : 'Show'} footnote`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="industries-select-wrapper">
        <div className="add-companies-view">
          <div className="add-companies-description">
            <h6>Add companies</h6>
          </div>
          <div className="submit-btns-container">
            <p>
              Click &apos;Add industries&apos; to add pre-curated industry list of companies. To
              add individual companies or to add companies in bulk, click &apos;Add
              companies&apos;.
            </p>
            <Button
              className="industries-btn"
              onClick={() => {
                setIndustryError(false);
                setShowIndustriesModal(true);
              }}
            >
              <AddBusinessOutlinedIcon />
              Add industries
            </Button>
            <Button
              className="submit-btn"
              onClick={() => {
                setIndustryError(false);
                setShowAddCompModal(true);
              }}
            >
              <AddIcon />
              Add companies
            </Button>
          </div>
        </div>
      </div>

      {/* Match ASC 820: spinner while loading; no empty blue banner when there are zero comps */}
      {!showComps && !noCompsToShow && (
        <div className="loading-comps">
          <div className="dots-circle-spinner" />
        </div>
      )}
      {showComps && (
        <>
          <div className="comps-wrapper">
            <div className="comps-header">
              <div className="comps-header-text">
                <div>
                  {tabSelected === 'industry' && <h6>Select volatility guideline companies</h6>}
                  {tabSelected === 'comps' && <h6>Select comparable companies</h6>}
                  <p>
                    Click the checkbox next to a company&apos;s name to select and deselect
                    individual companies. To select or deselect all, click the checkbox in the
                    Company list header.
                  </p>
                </div>
                {identifyingOutliersSuccess ? (
                  <div className="outlier-status">
                    <CheckCircleOutlineIcon />
                    Outliers flagged
                  </div>
                ) : identifyingOutliers ? (
                  <div className="outlier-status">
                    <span className="dots-circle-spinner" />
                    Checking for outliers
                  </div>
                ) : (
                  <Tooltip
                    title={
                      <>
                        This will override any currently selected outliers.
                        <br />
                        It will automatically flag outliers using the volatility&apos;s
                        <br />
                        Inter-Quartile (IQR) method.
                      </>
                    }
                    PopperProps={{ className: 'bottom-arrow-tooltip iqr-tooltip' }}
                    placement="top"
                    arrow
                  >
                    <Button
                      onClick={() => identifyOutliers()}
                      disabled={noCompsToShow || runningComps || loadingComps}
                    >
                      <FlagIcon />
                      Check for outliers
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>

            <FilterMenus
              tabSelected={tabSelected}
              growthFilterOptions={growthFilterOptions}
              multiplesFilterOptions={multiplesFilterOptions}
              yearsFilterOptions={yearsFilterOptions}
              fiscalPeriodFilterOptions={fiscalPeriodFilterOptions}
              filters={filters}
              setFilters={setFilters}
              descriptionsToShow={descriptionsToShow}
              setDescriptionsToShow={setDescriptionsToShow}
            />

            <CompTable
              tabSelected={tabSelected}
              compData={compData}
              setCompData={setCompData}
              setCompDataWithSort={setCompDataWithSort}
              deleteCompById={deleteCompById}
              filters={filters}
              valuationDate={valuationDate}
              setShowAddCompModal={setShowAddCompModal}
              headerScroll={headerScroll}
              subCompScroll={subCompScroll}
              overlayScrollbarRef={overlayScrollbarRef}
              rightCompXScrollRef={rightCompXScrollRef}
              rightCompYScroll={rightCompYScroll}
              rightCompYScrollRef={rightCompYScrollRef}
              reSummarize={reSummarize}
              summariesOutdated={summariesOutdated}
              runningComps={runningComps || loadingComps}
              descriptionsToShow={descriptionsToShow}
              setDescriptionsToShow={setDescriptionsToShow}
              descriptionsRef={descriptionsRef}
              checkboxesRef={checkboxesRef}
            />

            <SummariesTable
              tabSelected={tabSelected}
              compData={compData}
              summariesData={displaySummaries}
              valuationDate={valuationDate}
              reSummarizing={reSummarizing}
              reSummarizeRequest={reSummarizeRequest}
              reSummarizeComplete={reSummarizeComplete}
              summariesOutdated={summariesOutdated}
              filters={filters}
              summariesScrollRef={summariesScrollRef}
            />
          </div>
          {tabSelected === 'comps' && (
            <EquityInputs
              userData={userData}
              setUserData={setUserData}
              setSelectedGridValue={() => {}}
            />
          )}
        </>
      )}

      <AddCompModal
        showAddCompModal={showAddCompModal}
        setShowAddCompModal={setShowAddCompModal}
        showIndustriesModal={showIndustriesModal}
        setShowIndustriesModal={setShowIndustriesModal}
        runningComps={runningComps}
        removingIndustry={false}
        runComps={runComps}
        setIndustryError={setIndustryError}
        industryError={industryError}
        industries={industries}
        setIndustries={setIndustries}
        searchIndustryInput={searchIndustryInput}
        setSearchIndustryInput={setSearchIndustryInput}
        currentIndustries={currentIndustries}
        removeIndustryFromComp={removeIndustryFromComp}
        compRunProgress={compRunProgress}
      />

      <RefreshCompsModal
        showRefreshCompsModal={showRefreshCompsModal}
        setShowRefreshCompsModal={setShowRefreshCompsModal}
        runComps={runComps}
      />
    </div>
  );
}

Comps.propTypes = {
  mode: PropTypes.oneOf(['comps', 'industry']),
  companyToViewData: PropTypes.object,
};

Comps.defaultProps = {
  mode: 'comps',
  companyToViewData: {},
};
